import { useInfiniteQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import type { TableState } from './TableState';
import { useTableData } from './TableDataProvider';
import type { CommonData } from './createTableInfiniteDataSource';
import { makeFiltersOptional } from './makeFiltersOptional';

// Once the user has scrolled within 500px of the bottom of the table, fetch more data if we can
const SCROLL_THRESHOLD = 500;
const LOAD_SIZE = 30;

export const useTableInfiniteQuery = <TData extends CommonData = CommonData>() => {
  const { dataSource } = useTableData();
  const [filters, ordering] = useWatch<TableState, ['filters', 'ordering']>({ name: ['filters', 'ordering'] });

  const { data, isFetching, isLoading, isFetchingNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey: [...dataSource.queryKey, JSON.stringify(filters), JSON.stringify(ordering)],
    queryFn: ({ pageParam = 0 }) =>
      dataSource.queryFn({
        take: (pageParam + 1) * LOAD_SIZE,
        skip: pageParam * LOAD_SIZE,
        filters: makeFiltersOptional(filters),
        ordering: ordering ?? undefined,
      }),
    initialPageParam: 0,
    refetchOnWindowFocus: false,
    getNextPageParam: (lastPage, allPages) => {
      const total = lastPage.result.total ?? 0;
      const loadedCount = allPages.reduce((sum, page) => sum + (page.result.data?.length ?? 0), 0);
      return loadedCount < total ? allPages.length : undefined;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const flatData = useMemo(() => data?.pages.flatMap((page) => page.result.data ?? []) ?? [], [data]);

  const dataLength = flatData.length;
  const total = data?.pages[0]?.result.total ?? 0;

  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (!containerRefElement) {
        return;
      }

      const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
      if (scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD && !isFetching && dataLength < total) {
        void fetchNextPage();
      }
    },
    [isFetching, fetchNextPage, dataLength, total],
  );

  return useMemo(() => {
    return {
      showSkeleton: isLoading || isFetchingNextPage,
      data: flatData as TData['result'][],
      dataPages: (data?.pages ?? []) as TData[],
      dataLength,
      dataTotalLength: total,
      fetchMoreOnBottomReached,
    };
  }, [flatData, dataLength, fetchMoreOnBottomReached, total, isLoading, isFetchingNextPage, data?.pages]);
};
